/**
 * Created by Bowen on 5/24/2020.
 * Description:
 * Contact Header
 *
 * ------ maintenance history ------
 */
import { Component, OnInit, Input, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Observable, Subscription, fromEvent } from 'rxjs';
import { ContactDetailConfig } from '../../contact-detail/contact-detail.config';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { ContextMenuConfig } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { SystemUser } from '../../../tamalelibs/models/user.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { ScreenHelperService } from '../../../services/screen-helper.service';
import { ContactTemplateService } from '../../contact-dialog-new/contact-template.service';
import { ContactTemplateOpenOptions, ContactTemplateType, CompanyAction } from '../../contact-dialog-new/contact-dialog.model';
import { EntityType } from '../../../tamalelibs/models/entity-type.model';
import { Store, select } from '@ngrx/store';
import { lastCustomActionSelector } from '../../../redux/reducers/action-history.reducer';
import { EDIT_CONTACT_SUCCESS } from '../../../tamalelibs/redux/app.actions';
import { Contact } from '../../../tamalelibs/models/contact.model';
import { SlideSheetActionTypes, SlideSheetEventTypes } from '../../slide-sheet/slide-sheet.model';
import { SlideSheetService } from '../../../services/slide-sheet.service';

@Component({
    selector: 'tam-contact-header',
    templateUrl: './contact-new-header.component.html',
    styleUrls: ['./contact-new-header.component.scss']
})
export class ContactNewHeaderComponent implements OnInit, OnDestroy {
    @Input() config: ContactDetailConfig;
    @ViewChild(TooltipDirective, { static: false })
    public toolTipDir: TooltipDirective;

    contextMenuConfig: ContextMenuConfig;
    isPPCUser = false;
    contactStatus$: Observable<string>;
    // below four information will show on page
    jobTitlesName = '';
    jobFunction = '';
    emailAddress = '';
    phoneNumber = '';
    // this var is for getting window's width to change the header style
    windowSize: number;
    // this var is for job title, company and so on
    basicInformation: Array<any>;
    isIPAD = false;

    private _currentUser: SystemUser;
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _deviceService: DeviceDetectorService,
        private _storeQuerier: StoreQuerierService,
        private _screenHelperService: ScreenHelperService,
        private _contactTemplateService: ContactTemplateService,
        private _store: Store<any>,
        private _changeDetectorRef: ChangeDetectorRef,
        private _slideSheetService: SlideSheetService,
    ) {
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        this._currentUser = this._storeQuerier.getCurrentUser();
        // PPC user can save
        this.isPPCUser = this._currentUser.credential.publishPubConfiguration;
        // get the job title, company and so on: basic information
        this._getContactInformation(this.config.contact);
        this._initBasicInformation();
        // to init the header layout
        this.windowSize = window.innerWidth;
        this._changeBasicInformationLayout();
        this._destroySubscriptions.push(
            fromEvent(window, 'resize').subscribe((e: any) => {
                this.windowSize = e.target.innerWidth;
                this._changeBasicInformationLayout();
            }),

            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),

            this._contactTemplateService.actionSubject$.subscribe(res => {
                this._actionMessageHandler(res);
            }),

            // Listening the change of the contact
            this._slideSheetService.slideSheetFeedbackSubject$.subscribe(res => {
                if (res.type === SlideSheetEventTypes.CONTACT_VALUE_CHANGE) {
                    this.config.contact = res.payload;
                    this._getContactInformation(this.config.contact);
                    this._initBasicInformation();
                    this._changeDetectorRef.detectChanges();
                }
            })
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    editContact() {
        const _contactDialogOpenOptions = new ContactTemplateOpenOptions();
        _contactDialogOpenOptions.openType = ContactTemplateType.EditContact;
        _contactDialogOpenOptions.contact = this.config.contact;
        if (this.config.contact) {
            this._contactTemplateService.dialogOpen$.next(_contactDialogOpenOptions);
        }
        if (this.isIPAD) {
            this.toolTipDir.showOn = 'none';
        }
    }

    onSaveClick() {
        this._screenHelperService.saveScreen$.next();
    }

    onEntityClick(event, id) {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ENTITY_PANEL,
            payload: id
        });
    }

    canEdit() {
        // if entity is team, need manageTeam credential
        if (this.config.contact.type.id === EntityType.TEAM.id) {
            return this._currentUser.credential.manageTeam;
        } else {
            return true;
        }
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _actionMessageHandler(action) {
        if (action.type === EDIT_CONTACT_SUCCESS) {
            this._getContactInformation(action.payload);
            // only in this way, we can avoid to compute the width again to improve performance
            this.basicInformation[0].value = action.payload.companyWeb.name;
            this.basicInformation[0].id = action.payload.companyWeb.id;
            this.basicInformation[0].type = action.payload.companyWeb.id ? 'Entity' : 'Text';
            this.basicInformation[1].value = this.jobTitlesName;
            this.basicInformation[2].value = this.jobFunction;
            this.basicInformation[3].value = this.emailAddress;
            this.basicInformation[3].id = this.emailAddress;
            this.basicInformation[4].value = this.phoneNumber;
            this._changeDetectorRef.detectChanges();
        }
        if (action.type === CompanyAction.CreateInvalidCompany) {
            this.basicInformation[0].value = (action.payload.targetCompany && action.payload.targetCompany.name) ? action.payload.targetCompany.name : '';
            this.basicInformation[0].id = (action.payload.targetCompany && action.payload.targetCompany.id) ? action.payload.targetCompany.id : '';
            this.basicInformation[0].type = (action.payload.targetCompany && action.payload.targetCompany.id) ? 'Entity' : 'Text';
        }
    }

    private _changeBasicInformationLayout() {
        if (this.windowSize <= 1450 && this.windowSize >= 1300) {
            this.basicInformation[this.basicInformation.length - 1].display = 'none';
            this.basicInformation[this.basicInformation.length - 2].display = '';
            this.basicInformation[this.basicInformation.length - 3].display = '';
            this.basicInformation.map(element => {
                element.width = 100 / (this.basicInformation.length - 1) + '%';
            });
        } else if (this.windowSize <= 1300 && this.windowSize >= 1150) {
            this.basicInformation[this.basicInformation.length - 1].display = 'none';
            this.basicInformation[this.basicInformation.length - 2].display = 'none';
            this.basicInformation[this.basicInformation.length - 3].display = '';
            this.basicInformation.map(element => {
                element.width = 100 / (this.basicInformation.length - 2) + '%';
            });
        } else if (this.windowSize <= 1150 && this.windowSize >= 1000) {
            this.basicInformation[this.basicInformation.length - 1].display = 'none';
            this.basicInformation[this.basicInformation.length - 2].display = 'none';
            this.basicInformation[this.basicInformation.length - 3].display = 'none';
            this.basicInformation.map(element => {
                element.width = 100 / (this.basicInformation.length - 3) + '%';
            });
        } else if (this.windowSize >= 1450) {
            this.basicInformation[this.basicInformation.length - 1].display = '';
            this.basicInformation[this.basicInformation.length - 2].display = '';
            this.basicInformation[this.basicInformation.length - 3].display = '';
            this.basicInformation.map(element => {
                element.width = 100 / this.basicInformation.length + '%';
            });
        }
    }

    private _getContactInformation(contact: Contact) {
        // get contact job title
        if (contact.jobTitleWeb && contact.jobTitleWeb.length > 0) {
            this.jobTitlesName = contact.jobTitleWeb.filter(item => item.id).map(res => res.name).join(', ');
        } else {
            this.jobTitlesName = '';
        }

        // get contact job function
        if (this.config.contact.jobFunction && contact.jobFunction.length > 0) {
            this.jobFunction = contact.jobFunction.map(res => res.name).join(', ');
        } else {
            this.jobFunction = '';
        }

        // get email
        if (contact.webEmail && contact.webEmail.filter(item => !!item.primaryEmail)[0]) {
            this.emailAddress = contact.webEmail.filter(item => !!item.primaryEmail)[0].address;
        } else {
            this.emailAddress = '';
        }

        // get phone number
        if (contact.webPhone && contact.webPhone.filter(item => item.name === 'Work')[0]) {
            this.phoneNumber = contact.webPhone.filter(item => item.name === 'Work')[0].phone;
        } else {
            this.phoneNumber = '';
        }
    }

    private _initBasicInformation() {
        this.basicInformation = [{
            title: 'Company',
            value: this._getCompanyContent(),
            id: this.config.contact.companyWeb && this.config.contact.companyWeb.id || '',
            type: this.config.contact.companyWeb && this.config.contact.companyWeb.id && this.config.contact.companyWeb.visible ? 'Entity' : 'Text',
            display: '',
            width: '20%'
        },
        {
            title: 'Job Title',
            value: this.jobTitlesName,
            id: '',
            type: 'Text',
            display: '',
            width: '20%'
        },
        {
            title: 'Job Function',
            value: this.jobFunction,
            id: '',
            type: 'Text',
            display: '',
            width: '20%'
        },
        {
            title: 'Primary Email',
            value: this.emailAddress,
            id: this.emailAddress,
            type: 'Email',
            display: '',
            width: '20%'
        },
        {
            title: 'Work Phone',
            value: this.phoneNumber,
            id: '',
            type: 'Text',
            display: '',
            width: '20%'
        }];
    }

    // According new requirement,invalid company also need to display.
    private _getCompanyContent() {
        if (this.config.contact.companyWeb && this.config.contact.companyWeb.name) {
            if (this.config.contact.companyWeb.visible === false) {
                // If from RC and set invisible.will hide company by ******;
                return '******';
            } else {
                if (this.config.contact.companyWeb.id) {
                    return this.config.contact.companyWeb.name;
                } else {
                    return `${this.config.contact.companyWeb.name} *`;
                }
            }
        } else {
            return '';
        }
    }
}
